import Phaser from "phaser";
import type { CastleDamageStage } from "../../../../shared/types/BattleEvent";

export class Castle {
  readonly sprite: Phaser.GameObjects.Image;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly damageOverlay: Phaser.GameObjects.Graphics;
  private readonly hpFrame: Phaser.GameObjects.Image;
  private readonly hpBack: Phaser.GameObjects.Rectangle;
  private readonly hpFill: Phaser.GameObjects.Rectangle;
  private readonly label: Phaser.GameObjects.Text;
  private readonly labelPrefix: string;
  // Visual position/scale of the sprite (NOT the gameplay coordinate, which lives in
  // mapConfig). Mutable so the in-game castle-edit tool can drag/resize the keep.
  private x: number;
  private y: number;
  private visualScale: number;
  private readonly baseTint: number;
  private readonly baseTexture: string;
  // Pre-rendered "battered" sprite shown once the keep is heavily damaged, so a
  // ruined castle reads as ruined art instead of a tinted intact sprite with a
  // loud vector ring drawn over it. Undefined if the asset was not loaded.
  private readonly damagedTexture?: string;
  hp: number;
  readonly maxHp: number;
  damageStage: CastleDamageStage = 0;

  constructor(scene: Phaser.Scene, x: number, y: number, texture: string, label: string, tint: number, maxHp = 100, scale = 0.62) {
    this.hp = maxHp;
    this.maxHp = maxHp;
    this.labelPrefix = label;
    this.x = x;
    this.y = y;
    this.visualScale = scale;
    this.baseTint = tint;
    this.baseTexture = texture;
    const damagedKey = `${texture}_damaged`;
    this.damagedTexture = scene.textures.exists(damagedKey) ? damagedKey : undefined;
    this.shadow = scene.add.ellipse(x, y + 54, 126, 30, 0x030305, 0.38).setDepth(35);
    this.sprite = scene.add.image(x, y, texture).setDepth(38).setScale(scale);
    this.damageOverlay = scene.add.graphics().setDepth(39);
    this.hpFrame = scene.add.image(x, y - 76, "hp_frame").setDepth(90).setDisplaySize(146, 18);
    this.hpBack = scene.add.rectangle(x, y - 76, 126, 7, 0x15151d, 0.95).setDepth(91);
    this.hpFill = scene.add.rectangle(x - 63, y - 76, 126, 7, tint, 0.95).setOrigin(0, 0.5).setDepth(92);
    this.label = scene.add
      .text(x, y - 99, `${label} ${maxHp}`, {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "14px",
        color: "#f6f0df",
        stroke: "#111016",
        strokeThickness: 3
      })
      .setOrigin(0.5)
      .setDepth(92);
  }

  /** Current visual transform (sprite position + scale), for the edit tool to read/save. */
  getVisualTransform() {
    return { x: this.x, y: this.y, scale: this.visualScale };
  }

  /** Move + resize all visual parts of the keep. Gameplay coordinate is unaffected. */
  setVisualTransform(x: number, y: number, scale: number) {
    this.x = x;
    this.y = y;
    this.visualScale = scale;
    this.shadow.setPosition(x, y + 54);
    this.sprite.setPosition(x, y).setScale(scale);
    this.hpFrame.setPosition(x, y - 76);
    this.hpBack.setPosition(x, y - 76);
    this.hpFill.setPosition(x - 63, y - 76);
    this.label.setPosition(x, y - 99);
    this.updateBar();
    this.drawDamageOverlay();
  }

  takeDamage(amount: number) {
    if (this.hp <= 0) return;
    this.hp = Math.max(0, this.hp - amount);
    this.sprite.setTintFill(0xffffff);
    this.sprite.scene.time.delayedCall(85, () => this.sprite.clearTint());
    this.updateDamageStage();
    this.updateBar();
  }

  heal(amount: number) {
    const wasDestroyed = this.hp <= 0;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    if (wasDestroyed && this.hp > 0) {
      this.sprite.clearTint().setAlpha(1);
    }
    this.updateDamageStage();
    this.updateBar();
  }

  setDestroyedTint() {
    this.damageStage = 4;
    this.applyStageTexture();
    // Smoke-darkened ruin rather than a flat grey + neon ring.
    this.sprite.setTint(0x6b6360).setAlpha(0.9);
    this.drawDamageOverlay();
  }

  // Swap between the intact and battered sprite based on how wrecked the keep is.
  private applyStageTexture() {
    const wantDamaged = this.damageStage >= 3 && this.damagedTexture !== undefined;
    const targetKey = wantDamaged ? this.damagedTexture! : this.baseTexture;
    if (this.sprite.texture.key !== targetKey) {
      this.sprite.setTexture(targetKey);
    }
  }

  private updateBar() {
    this.hpFill.width = 126 * Math.max(0, this.hp / this.maxHp);
    this.hpFill.setFillStyle(this.stageColor(), 0.95);
    this.label.setText(`${this.labelPrefix} ${Math.ceil(this.hp)}`);
  }

  private updateDamageStage() {
    const nextStage = this.stageForHp();
    if (nextStage === this.damageStage) return;
    this.damageStage = nextStage;
    this.applyStageTexture();
    this.drawDamageOverlay();
  }

  private stageForHp(): CastleDamageStage {
    if (this.hp <= 0) return 4;
    const ratio = this.hp / this.maxHp;
    if (ratio <= 0.25) return 3;
    if (ratio <= 0.5) return 2;
    if (ratio <= 0.75) return 1;
    return 0;
  }

  private stageColor() {
    if (this.damageStage >= 3) return 0xff604f;
    if (this.damageStage === 2) return 0xffb15a;
    if (this.damageStage === 1) return 0xffd36e;
    return this.baseTint;
  }

  private drawDamageOverlay() {
    this.damageOverlay.clear();
    if (this.damageStage <= 0) {
      this.sprite.setAlpha(1);
      return;
    }
    const crackColor = this.damageStage >= 3 ? 0xff604f : 0xf0d49a;
    this.damageOverlay.lineStyle(2, crackColor, 0.72);
    this.damageOverlay.beginPath();
    this.damageOverlay.moveTo(this.x - 16, this.y - 42);
    this.damageOverlay.lineTo(this.x - 6, this.y - 20);
    this.damageOverlay.lineTo(this.x - 14, this.y + 2);
    this.damageOverlay.lineTo(this.x + 4, this.y + 26);
    this.damageOverlay.strokePath();
    this.damageOverlay.beginPath();
    this.damageOverlay.moveTo(this.x + 22, this.y - 34);
    this.damageOverlay.lineTo(this.x + 9, this.y - 12);
    this.damageOverlay.lineTo(this.x + 20, this.y + 10);
    this.damageOverlay.strokePath();

    if (this.damageStage >= 2) {
      this.damageOverlay.fillStyle(0x3a3330, 0.42);
      this.damageOverlay.fillCircle(this.x - 26, this.y - 18, 12);
      this.damageOverlay.fillCircle(this.x + 28, this.y + 16, 10);
      this.damageOverlay.lineStyle(3, 0x1f1d22, 0.52);
      this.damageOverlay.strokeCircle(this.x - 2, this.y + 36, 18);
    }

    if (this.damageStage >= 3) {
      // Drifting smoke + glowing embers at the base — no bright vector ring that
      // reads as a stray UI graphic floating over the keep.
      this.damageOverlay.fillStyle(0x1c1a1d, 0.5);
      this.damageOverlay.fillCircle(this.x - 18, this.y - 30, 16);
      this.damageOverlay.fillCircle(this.x + 16, this.y - 38, 13);
      this.damageOverlay.fillCircle(this.x + 2, this.y - 48, 11);
      this.damageOverlay.fillStyle(0xff7a3c, 0.32);
      this.damageOverlay.fillCircle(this.x - 30, this.y + 30, 5);
      this.damageOverlay.fillCircle(this.x + 30, this.y + 24, 4);
      this.damageOverlay.fillStyle(0xffd36e, 0.28);
      this.damageOverlay.fillCircle(this.x + 6, this.y + 34, 3);
    }
  }
}
